import type { ReactNode } from 'react';
import { formatBudgetCompact } from '../lib/format';

/**
 * A value slot on a LOCKED Pro card: the real card's typography, a blurred
 * placeholder instead of the number.
 *
 * Three rules, and each exists because of a way this could mislead:
 *
 * 1. **The placeholder is obviously fake once un-blurred.** Every digit is an
 *    `8` — «88.8%», «88 млн», «8 из 88 контрактов». A visitor who removes the
 *    blur in devtools (and someone always does) must land on nonsense, not on a
 *    number they could mistake for this tender's real one. The server never
 *    sends the real values at all; this is the second line, not the first.
 * 2. **It is invisible to assistive tech and to the cursor** — `aria-hidden`,
 *    `pointer-events: none`, `user-select: none` — so nothing selects or reads
 *    out a fabricated figure. The card states «Доступно на Pro» once instead
 *    (`AnalyticsTeaser`).
 * 3. **It is deterministic.** No randomness anywhere, so SSR and hydration
 *    produce byte-identical markup and the prerendered page does not flicker.
 *
 * `data-teaser-placeholder` is a marker only (no value in it) — it is what the
 * leak test walks to assert every placeholder is all-8s and aria-hidden.
 */
export function BlurredValue({
  children,
  className,
  blurPx = 4,
}: {
  children: ReactNode;
  className?: string;
  /** Blur radius. 4 px everywhere except inside a pill, where the halo is wider
   *  than the pill's 8 px padding and reads as a smudged border even with the
   *  pill clipping it. */
  blurPx?: number;
}) {
  return (
    <span
      aria-hidden
      data-teaser-placeholder=""
      className={`pointer-events-none select-none${className ? ` ${className}` : ''}`}
      style={{ filter: `blur(${blurPx}px)` }}
    >
      {children}
    </span>
  );
}

/** The one digit any placeholder may contain — see rule 1 above. */
export const PLACEHOLDER_DIGIT = '8';

/** A share, in the real cards' `toFixed(1)` style. */
export const PLACEHOLDER_PCT = '88.8%';

/** A whole count (participants, contracts). */
export const PLACEHOLDER_COUNT = '88';

/** A Herfindahl index — four digits, like the real pill. */
export const PLACEHOLDER_HHI = '8888';

/** A procedure / lot number, as the re-listing row would show one. */
export const PLACEHOLDER_ID = '#888888';

/** A compact amount through the REAL formatter, so the unit word and the
 *  locale's separator are whatever the unlocked card would print («88 млн» /
 *  «88 mln»). `magnitude` picks the order of size; both inputs are all-8s. */
export function placeholderAmount(locale: string, magnitude: 'small' | 'large' = 'large'): string {
  return formatBudgetCompact(magnitude === 'large' ? '88000000' : '8000000', locale) ?? '88';
}
